import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Building2, MapPin, ArrowUpRight, Info, Download } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

function fmtInt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}
function fmtPct(v, d = 1) {
  if (v == null) return '—';
  return `${Number(v).toFixed(d)}%`;
}

function Stat({ value, label, accent, sub }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
      <div className={`font-display text-2xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
      {sub && <div className="mt-1 text-[11px] text-[#5A6660]">{sub}</div>}
    </div>
  );
}

export default function ManagedCarePayersPage() {
  const [d, setD] = useState(null);
  const [kpi, setKpi] = useState(null); // KPI tiles are based on the enrolled-sorted snapshot
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('enrolled');

  useEffect(() => {
    // Always fetch the enrolled-sorted snapshot once for invariant KPI tiles
    api.maPayers({ sort: 'enrolled', limit: 100 }).then(setKpi).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.maPayers({ sort, limit: 100 }).then(setD).finally(() => setLoading(false));
  }, [sort]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[
          { label: 'Home', to: '/' },
          { label: 'Managed Care', to: '/managed-care' },
          { label: 'Payers' },
        ]} />

        <header className="mt-6 max-w-3xl">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
            <Building2 size={12} className="text-[#4A7C5E]"/>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">MA payer rollup</span>
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight leading-tight">
            Who owns Medicare Advantage?
          </h1>
          <p className="mt-3 text-[14px] text-[#3A4A44] leading-relaxed">
            Every organization holding an active MA contract in the United States, ranked by enrollment
            and geographic footprint. Sourced from the CMS Monthly Enrollment by State/County/Contract file.
          </p>
        </header>

        {loading && <div className="mt-8 text-sm text-[#7A857F]">Loading payer rollup…</div>}

        {!loading && d && (
          <>
            <section className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payer-stats">
              <Stat value={fmtInt((kpi || d).payers_indexed)} label="MA contract holders" accent="text-[#4A7C5E]" />
              <Stat value={fmtInt((kpi || d).national_total_enrolled)} label="Total MA enrolled" />
              <Stat value={(kpi || d).items?.[0]?.organization_name_display?.split(/[,(]/)[0] || '—'} label="Largest payer"
                    sub={(kpi || d).items?.[0] ? `${fmtPct((kpi || d).items[0].national_share_pct, 2)} national share` : ''} />
              <Stat value={`${((kpi || d).items || []).slice(0, 5).reduce((a, b) => a + (b.national_share_pct || 0), 0).toFixed(1)}%`} label="Top-5 concentration" />
            </section>

            <div className="mt-6 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1">
                {[
                  { id: 'enrolled', label: 'By enrollment' },
                  { id: 'footprint', label: 'By counties served' },
                  { id: 'states', label: 'By states served' },
                  { id: 'name', label: 'By name' },
                ].map((t) => (
                  <button key={t.id} onClick={() => setSort(t.id)}
                          data-testid={`payer-sort-${t.id}`}
                          className={`px-3 py-1.5 rounded-md text-[12.5px] ${sort === t.id ? 'bg-[#1A2622] text-[#FBF6EC]' : 'bg-white border border-[#E5DCC4] text-[#3A4A44] hover:border-[#4A7C5E]'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="text-[11.5px] text-[#7A857F]">{(d.items || []).length} of {fmtInt(d.payers_indexed)} payers</div>
            </div>

            <section className="mt-3 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                  <tr>
                    <th className="text-left px-4 py-2.5 w-10">#</th>
                    <th className="text-left px-4 py-2.5">Organization</th>
                    <th className="text-right px-4 py-2.5">Enrolled</th>
                    <th className="text-right px-4 py-2.5 hidden md:table-cell">States</th>
                    <th className="text-right px-4 py-2.5 hidden md:table-cell">Counties</th>
                    <th className="text-right px-4 py-2.5">Share %</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.items || []).map((p, i) => (
                    <tr key={p.organization_name_norm} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`payer-row-${i}`}>
                      <td className="px-4 py-2.5 text-[#7A857F]">{i + 1}</td>
                      <td className="px-4 py-2.5">
                        <Link to={`/managed-care/payer/${encodeURIComponent(p.organization_name_norm)}`} className="text-[#1A2622] font-medium hover:text-[#2D5F4F]">
                          {p.organization_name_display}
                        </Link>
                        <div className="text-[10.5px] text-[#7A857F] mt-0.5">{p.contract_count} contracts</div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmtInt(p.total_enrolled)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums hidden md:table-cell">{p.states_served}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums hidden md:table-cell">{fmtInt(p.counties_served)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="inline-block min-w-[42px] text-right font-semibold text-[#1A2622]">{fmtPct(p.national_share_pct, 2)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA]">
              <Info size={12} className="mt-0.5 shrink-0"/>
              <span>Source: {d.source}. Counts roll up every plan/contract held by each organization across all US counties.
              Excludes territories. Enrollment counts ≤ 10 are excluded from the underlying CMS file (HIPAA suppression).</span>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
